import React from 'react';

type Row = {
    id: string;
    bitrateKbps: number | null;
    fps: number | null;
    width: number | null;
    height: number | null;
    packetsLost: number | null;
    packetsReceived: number | null;
    packetLossPct: number | null;
    jitterMs: number | null;
    rttMs: number | null;
};
type Prev = {
    bytes?: number;
    ts: number;
    packetsLost?: number;
    packetsTotal?: number;
};
type ScreenContentKind = 'static' | 'dynamic';
type QoEContentHint = ScreenContentKind | 'unknown';

function normalizeHigherIsBetter(value: number, good: number, bad: number): number {
    if (!Number.isFinite(value)) return 0.5;
    if (value >= good) return 1;
    if (value <= bad) return 0;
    return (value - bad) / (good - bad);
}
function normalizeLowerIsBetter(value: number, good: number, bad: number): number {
    if (!Number.isFinite(value)) return 0.5;
    if (value <= good) return 1;
    if (value >= bad) return 0;
    return 1 - (value - good) / (bad - good);
}
class QoESmoother {
    private readonly last = new Map<string, number>();
    constructor(private readonly alpha = 0.7) {}
    smooth(id: string, score: number): number {
        const prev = this.last.get(id);
        const base = prev ?? 100;
        const s = this.alpha * base + (1 - this.alpha) * score;
        this.last.set(id, s);
        return Math.round(s);
    }
}
function classifyQoE(score: number): 'good' | 'fair' | 'poor' {
    if (score >= 80) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
}

type QualityTone = 'good' | 'fair' | 'poor' | 'unknown';

function toneClass(tone: QualityTone): string {
    if (tone === 'good') return 'text-emerald-300';
    if (tone === 'fair') return 'text-amber-300';
    if (tone === 'poor') return 'text-rose-300';
    return 'text-slate-300';
}

function qualityForBitrate(kbps: number | null): QualityTone {
    if (kbps == null) return 'unknown';
    if (kbps >= 1500) return 'good';
    if (kbps >= 700) return 'fair';
    return 'poor';
}

function qualityForFps(fps: number | null): QualityTone {
    if (fps == null || fps <= 0) return 'unknown';
    if (fps >= 24) return 'good';
    if (fps >= 12) return 'fair';
    return 'poor';
}

function qualityForPacketLoss(lossPct: number | null): QualityTone {
    if (lossPct == null) return 'unknown';
    if (lossPct <= 1) return 'good';
    if (lossPct <= 3) return 'fair';
    return 'poor';
}

function qualityForJitter(jitterMs: number | null): QualityTone {
    if (jitterMs == null) return 'unknown';
    if (jitterMs <= 10) return 'good';
    if (jitterMs <= 30) return 'fair';
    return 'poor';
}

function qualityForRtt(rttMs: number | null): QualityTone {
    if (rttMs == null) return 'unknown';
    if (rttMs <= 80) return 'good';
    if (rttMs <= 180) return 'fair';
    return 'poor';
}

function formatBitrate(kbps: number | null): string {
    if (kbps == null || !Number.isFinite(kbps)) return '—';
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(2)} Mbps`;
    return `${kbps.toFixed(0)} kbps`;
}

function formatPacketLoss(count: number | null, pct: number | null): string {
    if (count == null) return '—';
    if (pct == null || !Number.isFinite(pct)) return `${count}`;
    return `${count} (${pct.toFixed(2)}%)`;
}

const QOE_PROBE_WIDTH = 64;
const QOE_PROBE_HEIGHT = 36;
const QOE_STATIC_DIFF_THRESHOLD = 0.02;
const QOE_CONTENT_PROBE_INTERVAL_MS = 800;

function calculateFrameDiff(frame1: ImageData, frame2: ImageData): number {
    if (frame1.data.length !== frame2.data.length || frame1.data.length === 0) return 1;
    let diff = 0;
    let n = 0;
    const step = 16;
    for (let i = 0; i < frame1.data.length; i += step) {
        diff += Math.abs(frame1.data[i]! - frame2.data[i]!);
        n++;
    }
    return n > 0 ? diff / n / 255 : 0;
}

function detectContentType(currentFrame: ImageData, lastFrame: ImageData | null): ScreenContentKind {
    if (!lastFrame) return 'dynamic';
    const d = calculateFrameDiff(lastFrame, currentFrame);
    return d < QOE_STATIC_DIFF_THRESHOLD ? 'static' : 'dynamic';
}

function getProbeContext(): CanvasRenderingContext2D | null {
    const c = document.createElement('canvas');
    c.width = QOE_PROBE_WIDTH;
    c.height = QOE_PROBE_HEIGHT;
    return c.getContext('2d', {willReadFrequently: true});
}

function useSenderContentType(video: HTMLVideoElement | null, active: boolean): QoEContentHint {
    const [kind, setKind] = React.useState<QoEContentHint>('unknown');
    const lastFrameRef = React.useRef<ImageData | null>(null);
    const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
    React.useEffect(() => {
        if (!active || !video) {
            lastFrameRef.current = null;
            setKind('unknown');
            return;
        }
        if (!ctxRef.current) ctxRef.current = getProbeContext();
        const ctx = ctxRef.current;
        if (!ctx) return;
        const tick = () => {
            if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
            try {
                ctx.drawImage(video, 0, 0, QOE_PROBE_WIDTH, QOE_PROBE_HEIGHT);
                const current = ctx.getImageData(0, 0, QOE_PROBE_WIDTH, QOE_PROBE_HEIGHT);
                const next = detectContentType(current, lastFrameRef.current);
                lastFrameRef.current = current;
                setKind(next);
            } catch {
                /* ignore */
            }
        };
        void tick();
        const id = window.setInterval(tick, QOE_CONTENT_PROBE_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [active, video]);
    return kind;
}

function useReceiverContentTypesByPeer(
    peerIds: readonly string[],
    getVideoForPeer: (peerId: string) => HTMLVideoElement | null,
    active: boolean
): ReadonlyMap<string, QoEContentHint> {
    const [map, setMap] = React.useState<Map<string, QoEContentHint>>(() => new Map());
    const lastByPeerRef = React.useRef<Map<string, ImageData>>(new Map());
    const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
    const peerKey = peerIds.join('\0');
    React.useEffect(() => {
        if (!active || peerIds.length === 0) {
            lastByPeerRef.current.clear();
            setMap(new Map());
            return;
        }
        const allowed = new Set(peerIds);
        for (const k of lastByPeerRef.current.keys()) if (!allowed.has(k)) lastByPeerRef.current.delete(k);
        if (!ctxRef.current) ctxRef.current = getProbeContext();
        const ctx = ctxRef.current;
        if (!ctx) return;
        const tick = () => {
            const nextMap = new Map<string, QoEContentHint>();
            for (const id of peerIds) {
                const video = getVideoForPeer(id);
                if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
                    nextMap.set(id, 'unknown');
                    continue;
                }
                try {
                    ctx.drawImage(video, 0, 0, QOE_PROBE_WIDTH, QOE_PROBE_HEIGHT);
                    const current = ctx.getImageData(0, 0, QOE_PROBE_WIDTH, QOE_PROBE_HEIGHT);
                    const prev = lastByPeerRef.current.get(id) ?? null;
                    const kind = detectContentType(current, prev);
                    lastByPeerRef.current.set(id, current);
                    nextMap.set(id, kind);
                } catch {
                    nextMap.set(id, 'unknown');
                }
            }
            setMap(nextMap);
        };
        void tick();
        const handle = window.setInterval(tick, QOE_CONTENT_PROBE_INTERVAL_MS);
        return () => window.clearInterval(handle);
    }, [active, peerKey, getVideoForPeer, peerIds]);
    return map;
}

function pickPair(stats: RTCStatsReport): any {
    let selected: any = null;
    stats.forEach((r: any) => {
        if (r.type !== 'candidate-pair' || r.state !== 'succeeded') return;
        if (r.nominated || selected == null) selected = r;
    });
    return selected;
}

async function collect(id: string, pc: RTCPeerConnection, role: 'sender' | 'receiver', prev?: Prev): Promise<{row: Row; next: Prev}> {
    const stats = await pc.getStats();
    const now = Date.now();
    let bytes: number | undefined;
    let fps: number | null = null;
    let width: number | null = null;
    let height: number | null = null;
    let packetsLost: number | null = null;
    let packetsReceived: number | null = null;
    let packetsTotal: number | null = null;
    let packetLossPct: number | null = null;
    let jitterMs: number | null = null;
    let rttMs: number | null = null;
    let outboundVideoId: string | null = null;
    let senderPacketsSent: number | null = null;

    stats.forEach((r: any) => {
        if (role === 'sender' && r.type === 'outbound-rtp' && r.kind === 'video') {
            outboundVideoId = r.id ?? outboundVideoId;
            bytes = r.bytesSent ?? bytes;
            fps = r.framesPerSecond ?? fps;
            senderPacketsSent = r.packetsSent ?? senderPacketsSent;
            width = r.frameWidth ?? width;
            height = r.frameHeight ?? height;
        }
        if (
            role === 'sender' &&
            r.type === 'remote-inbound-rtp' &&
            (r.localId === outboundVideoId || r.kind === 'video' || r.mediaType === 'video')
        ) {
            packetsLost = r.packetsLost ?? packetsLost;
            jitterMs = r.jitter != null ? r.jitter * 1000 : jitterMs;
            rttMs = r.roundTripTime != null ? r.roundTripTime * 1000 : rttMs;
        }
        if (role === 'receiver' && r.type === 'inbound-rtp' && r.kind === 'video') {
            bytes = r.bytesReceived ?? bytes;
            fps = r.framesPerSecond ?? fps;
            packetsLost = r.packetsLost ?? packetsLost;
            packetsReceived = r.packetsReceived ?? packetsReceived;
            packetsTotal = r.packetsReceived ?? packetsTotal;
            jitterMs = r.jitter != null ? r.jitter * 1000 : jitterMs;
            width = r.frameWidth ?? width;
            height = r.frameHeight ?? height;
        }
        if (r.type === 'track') {
            width = r.frameWidth ?? width;
            height = r.frameHeight ?? height;
        }
    });
    if (role === 'sender' && senderPacketsSent != null) {
        packetsTotal = senderPacketsSent;
    }
    const pair = pickPair(stats);
    if (pair?.currentRoundTripTime != null) rttMs = pair.currentRoundTripTime * 1000;
    let bitrateKbps: number | null = null;
    if (bytes != null && prev?.bytes != null) {
        const dt = Math.max(0.001, (now - prev.ts) / 1000);
        const d = bytes - prev.bytes;
        if (d >= 0) bitrateKbps = (d * 8) / 1000 / dt;
    } else if (pair?.availableOutgoingBitrate != null) {
        bitrateKbps = pair.availableOutgoingBitrate / 1000;
    }

    if (
        packetsLost != null &&
        packetsTotal != null &&
        prev?.packetsLost != null &&
        prev?.packetsTotal != null
    ) {
        const dLost = packetsLost - prev.packetsLost;
        const dTotal = packetsTotal - prev.packetsTotal;
        const denom = dLost + dTotal;
        if (dLost >= 0 && dTotal >= 0 && denom > 0) {
            packetLossPct = (dLost / denom) * 100;
        }
    }

    return {
        row: {id, bitrateKbps, fps, width, height, packetsLost, packetsReceived, packetLossPct, jitterMs, rttMs},
        next: {
            bytes,
            ts: now,
            packetsLost: packetsLost ?? undefined,
            packetsTotal: packetsTotal ?? undefined,
        },
    };
}

function calculateQoE(row: Row, contentHint: QoEContentHint, smoother: QoESmoother) {
    const contentType: ScreenContentKind = contentHint === 'static' ? 'static' : 'dynamic';
    const lossRatio =
        row.packetLossPct != null
            ? row.packetLossPct / 100
            : row.packetsLost != null && row.packetsReceived != null && row.packetsLost + row.packetsReceived > 0
              ? row.packetsLost / (row.packetsLost + row.packetsReceived)
              : null;
    const thresholds =
        contentType === 'static'
            ? {
                  bitrate: {good: 200, bad: 50},
                  fps: {good: 5, bad: 1},
                  packetLoss: {good: 0.01, bad: 0.05},
                  rtt: {good: 100, bad: 400},
                  jitter: {good: 10, bad: 50},
              }
            : {
                  bitrate: {good: 1500, bad: 300},
                  fps: {good: 30, bad: 10},
                  packetLoss: {good: 0.01, bad: 0.05},
                  rtt: {good: 50, bad: 300},
                  jitter: {good: 10, bad: 50},
              };
    const weights =
        contentType === 'static'
            ? {bitrate: 0.15, fps: 0.1, loss: 0.35, rtt: 0.25, jitter: 0.15}
            : {bitrate: 0.25, fps: 0.25, loss: 0.2, rtt: 0.15, jitter: 0.15};
    const s = {
        bitrate: row.bitrateKbps != null ? normalizeHigherIsBetter(row.bitrateKbps, thresholds.bitrate.good, thresholds.bitrate.bad) : null,
        fps: row.fps != null && row.fps > 0 ? normalizeHigherIsBetter(row.fps, thresholds.fps.good, thresholds.fps.bad) : null,
        loss: lossRatio != null ? normalizeLowerIsBetter(lossRatio, thresholds.packetLoss.good, thresholds.packetLoss.bad) : null,
        rtt: row.rttMs != null ? normalizeLowerIsBetter(row.rttMs, thresholds.rtt.good, thresholds.rtt.bad) : null,
        jitter: row.jitterMs != null ? normalizeLowerIsBetter(row.jitterMs, thresholds.jitter.good, thresholds.jitter.bad) : null,
    };
    let num = 0;
    let den = 0;
    const add = (score: number | null, weight: number) => {
        if (score != null && Number.isFinite(score) && weight > 0) {
            num += score * weight;
            den += weight;
        }
    };
    add(s.bitrate, weights.bitrate);
    add(s.fps, weights.fps);
    add(s.loss, weights.loss);
    add(s.rtt, weights.rtt);
    add(s.jitter, weights.jitter);
    const raw = den > 0 ? Math.round((num / den) * 100) : 0;
    const score = smoother.smooth(row.id, raw);
    const warnings: string[] = [];
    if (lossRatio != null && lossRatio > 0.05) warnings.push('High packet loss');
    if (contentHint !== 'static' && (row.fps ?? 0) > 0 && (row.fps ?? 0) < 10) warnings.push('Low frame rate');
    return {score, level: classifyQoE(score), warnings, contentType: contentHint};
}

function useWebRTCVideoStats(active: boolean, role: 'sender' | 'receiver', getPeers: () => Record<string, RTCPeerConnection>): Row[] {
    const [rows, setRows] = React.useState<Row[]>([]);
    const prevRef = React.useRef<Map<string, Prev>>(new Map());
    React.useEffect(() => {
        if (!active) {
            prevRef.current = new Map();
            setRows([]);
            return;
        }
        let cancelled = false;
        const tick = async () => {
            const peers = getPeers?.() ?? {};
            const entries = Object.entries(peers).filter(([, pc]) => pc && pc.connectionState !== 'closed' && pc.connectionState !== 'failed');
            const out: Row[] = [];
            const nextPrev = new Map<string, Prev>();
            for (const [id, pc] of entries) {
                try {
                    const {row, next} = await collect(id, pc, role, prevRef.current.get(id));
                    out.push(row);
                    nextPrev.set(id, next);
                } catch {
                    /* ignore */
                }
            }
            prevRef.current = nextPrev;
            if (!cancelled) setRows(out);
        };
        void tick();
        const h = window.setInterval(() => void tick(), 1000);
        return () => {
            cancelled = true;
            window.clearInterval(h);
        };
    }, [active, getPeers, role]);
    return rows;
}

export function WebRTCStatsFloating({
    role,
    getPeers,
    active,
    peerDisplayNames,
    remoteFallbackName,
    panelDisplayName,
    getSenderProbeVideo,
    getReceiverProbeVideo,
}: {
    role: 'sender' | 'receiver';
    getPeers: () => Record<string, RTCPeerConnection>;
    active: boolean;
    peerDisplayNames?: Readonly<Record<string, string>>;
    remoteFallbackName?: string;
    panelDisplayName?: string;
    getSenderProbeVideo?: () => HTMLVideoElement | null;
    getReceiverProbeVideo?: (peerId: string) => HTMLVideoElement | null;
}) {
    const [open, setOpen] = React.useState(false);
    const rows = useWebRTCVideoStats(active && open, role, getPeers);
    const smootherRef = React.useRef(new QoESmoother(0.7));
    const senderProbeVideo = role === 'sender' && getSenderProbeVideo ? getSenderProbeVideo() : null;
    const senderContentType = useSenderContentType(senderProbeVideo, active && open && role === 'sender');
    const peerIds = React.useMemo(() => rows.map((r) => r.id), [rows]);
    const getReceiverVideo = React.useCallback((id: string) => getReceiverProbeVideo?.(id) ?? null, [getReceiverProbeVideo]);
    const receiverContentByPeer = useReceiverContentTypesByPeer(peerIds, getReceiverVideo, active && open && role === 'receiver' && getReceiverProbeVideo != null);
    const headerTitle = React.useMemo(() => {
        const byPanel = panelDisplayName?.trim();
        if (byPanel) return `${byPanel} Statistics`;
        const byFallback = remoteFallbackName?.trim();
        if (byFallback) return `${byFallback} Statistics`;
        for (const row of rows) {
            const name = peerDisplayNames?.[row.id]?.trim();
            if (name) return `${name} Statistics`;
        }
        return 'Conference Room Statistics';
    }, [panelDisplayName, remoteFallbackName, rows, peerDisplayNames]);
    return (
        <div className="pointer-events-none fixed right-3 top-3 z-[1000] flex flex-col items-end gap-2">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`pointer-events-auto rounded-md border px-2.5 py-1 text-[12px] font-medium shadow-lg transition ${
                    open
                        ? 'border-sky-400/60 bg-black text-sky-100 ring-1 ring-sky-500/40'
                        : 'border-white/25 bg-black text-slate-100 hover:bg-neutral-950'
                }`}
                title="WebRTC statistics"
            >
                {open ? 'Hide stats' : 'Show stats'}
            </button>
            {open && (
                <div className="pointer-events-auto max-h-[min(520px,78vh)] w-[min(320px,calc(100vw-24px))] overflow-y-auto rounded-2xl border border-white/15 bg-[#080b11] p-3 text-[12px] text-slate-100 shadow-2xl ring-1 ring-white/10">
                    <div className="mb-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                            {headerTitle}
                        </div>
                        <div className="text-[10px] text-slate-400">streaming quality monitoring dashboard</div>
                    </div>
                    {rows.length === 0 ? (
                        <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-slate-400">
                            No active peer connection.
                        </div>
                    ) : (
                        rows.map((row) => {
                            const contentHint = role === 'sender' ? senderContentType : (receiverContentByPeer.get(row.id) ?? 'unknown');
                            const q = calculateQoE(row, contentHint, smootherRef.current);
                            const qColor = q.level === 'good' ? 'text-emerald-400' : q.level === 'fair' ? 'text-amber-300' : 'text-rose-400';
                            const mappedName = peerDisplayNames?.[row.id];
                            const title =
                                (mappedName && mappedName.trim()) ||
                                (remoteFallbackName && remoteFallbackName.trim()) ||
                                row.id.slice(0, 8);
                            const contentType = contentBadgeLabel(contentHint);
                            return (
                                <div
                                    key={row.id}
                                    className="mb-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 last:mb-0 transition-colors duration-300"
                                >
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <div className="max-w-[70%] truncate text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                                            {role === 'sender' ? 'Outbound' : 'Inbound'} · {title}
                                        </div>
                                        <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5 text-[9px] font-medium text-slate-300">
                                            {contentType}
                                        </span>
                                    </div>
                                    <div className={`mb-2 flex items-center justify-between rounded-lg border border-white/10 bg-black/35 px-2.5 py-1.5 text-[12px] font-semibold ${qColor}`}>
                                        <span className="text-slate-300">QoE</span>
                                        <span className="font-mono tabular-nums">
                                            {q.score} ● {q.level === 'good' ? 'Good' : q.level === 'fair' ? 'Warning' : 'Poor'}
                                        </span>
                                    </div>
                                    <div className="space-y-1.5">
                                        <MetricLine
                                            label="Bitrate"
                                            value={formatBitrate(row.bitrateKbps)}
                                            tone={qualityForBitrate(row.bitrateKbps)}
                                        />
                                        <MetricLine
                                            label="FPS"
                                            value={row.fps != null ? `${row.fps.toFixed(1)} fps` : '—'}
                                            tone={contentHint === 'dynamic' ? qualityForFps(row.fps) : 'unknown'}
                                        />
                                        <MetricLine
                                            label="Resolution"
                                            value={row.width && row.height ? `${row.width}×${row.height}` : '—'}
                                            tone="unknown"
                                        />
                                        <MetricLine
                                            label="Packet loss"
                                            value={formatPacketLoss(row.packetsLost, row.packetLossPct)}
                                            tone={qualityForPacketLoss(row.packetLossPct)}
                                        />
                                        <MetricLine
                                            label="Jitter"
                                            value={row.jitterMs != null ? `${row.jitterMs.toFixed(1)} ms` : '—'}
                                            tone={qualityForJitter(row.jitterMs)}
                                        />
                                        <MetricLine
                                            label="RTT"
                                            value={row.rttMs != null ? `${row.rttMs.toFixed(0)} ms` : '—'}
                                            tone={qualityForRtt(row.rttMs)}
                                        />
                                    </div>
                                    {q.warnings.length > 0 && <div className="mt-1 text-[10px] text-amber-400">{q.warnings.join(' · ')}</div>}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

function MetricLine({label, value, tone}: {label: string; value: string; tone: QualityTone}) {
    return (
        <div className="flex items-center justify-between gap-3 text-[12px] leading-snug transition-colors duration-300">
            <span className="font-medium text-slate-300">{label}</span>
            <span className={`font-mono tabular-nums ${toneClass(tone)}`}>{value}</span>
        </div>
    );
}

function contentBadgeLabel(contentType: QoEContentHint): string {
    if (contentType === 'static') return 'Static content';
    if (contentType === 'dynamic') return 'Video content';
    return 'Detecting content';
}

